import type { LabelFormat, Symbology } from './labels.ts';

export type SymbolImage = { readonly source: string; readonly modules: number };

export type SymbolOutcome =
  { readonly kind: 'drawn'; readonly image: SymbolImage } | { readonly kind: 'unsupported' };

export type DrawSymbol = (symbology: Symbology, text: string) => SymbolOutcome;

const CODE128_ALPHABET = /^[\x20-\x7E]+$/u;
const VIEW_BOX_WIDTH = /viewBox="0 0 ([\d.]+) /u;
const BAR_HEIGHT_MM = 10;

const imageOf = (svg: string): SymbolImage => ({
  source: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
  modules: Number(VIEW_BOX_WIDTH.exec(svg)?.[1] ?? 0),
});

export const loadSymbolDrawer = async (): Promise<DrawSymbol> => {
  const { code128, drawingSVG, qrcode } = await import('bwip-js');
  const drawers: Readonly<Record<Symbology, (text: string) => string>> = {
    code128: (text) =>
      code128({ bcid: 'code128', text, scale: 1, height: BAR_HEIGHT_MM, includetext: false }, drawingSVG()),
    qr: (text) => qrcode({ bcid: 'qrcode', text, scale: 1 }, drawingSVG()),
  };
  const drawn = new Map<string, SymbolOutcome>();
  const draw = (symbology: Symbology, text: string): SymbolOutcome =>
    symbology === 'code128' && !CODE128_ALPHABET.test(text)
      ? { kind: 'unsupported' }
      : { kind: 'drawn', image: imageOf(drawers[symbology](text)) };
  return (symbology, text) => {
    const key = `${symbology}:${text}`;
    const known = drawn.get(key) ?? draw(symbology, text);
    drawn.set(key, known);
    return known;
  };
};

const MIN_MODULE_MM = 0.25;
const QUIET_ZONE_MODULES = 20;
const USABLE_WIDTH_MM: Readonly<Record<LabelFormat, number>> = { sheet: 64, roll: 46 };

export const fitsOn = (image: SymbolImage, format: LabelFormat, symbology: Symbology): boolean =>
  symbology === 'qr' || (image.modules + QUIET_ZONE_MODULES) * MIN_MODULE_MM <= USABLE_WIDTH_MM[format];
